import { XIcon, ShieldAlertIcon, FilmIcon } from "lucide-react";
import { Link } from "react-router-dom";

import {
  useAcknowledgeEvent,
  useAlarmVerification,
  useEscalateEvent,
  useResolveEvent,
  type Severity,
} from "@/api/alerts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CameraFeedPlayer } from "@/components/CameraFeedPlayer";
import { cn, formatDate } from "@/lib/utils";

interface AlarmVerificationDrawerProps {
  alertId: string | null;
  open: boolean;
  onClose: () => void;
}

function severityVariant(s: Severity | string): "error" | "warning" | "online" {
  if (s === "critical") return "error";
  if (s === "warning") return "warning";
  return "online";
}

// SVG overlay drawing a normalised (0–1) bounding box over the snapshot.
function BoundingBoxOverlay({ box }: { box: Record<string, number> }) {
  const { x = 0, y = 0, w = 0, h = 0 } = box;
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
    >
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke="#f97316"
        strokeWidth={0.008}
      />
    </svg>
  );
}

export function AlarmVerificationDrawer({
  alertId,
  open,
  onClose,
}: AlarmVerificationDrawerProps) {
  const { data, isLoading, isError } = useAlarmVerification(open ? alertId : null);
  const ack = useAcknowledgeEvent();
  const resolve = useResolveEvent();
  const escalate = useEscalateEvent();

  if (!open) return null;

  const alert = data?.alert;
  const bbox = data?.detection_event?.bounding_box ?? null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Scrim */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      {/* Drawer */}
      <aside className="relative z-10 flex h-full w-[480px] max-w-full flex-col overflow-y-auto border-l bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2 font-semibold">
            <ShieldAlertIcon className="h-5 w-5 text-orange-500" />
            Alarm Verification
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {isLoading && (
          <div className="p-6 text-sm text-muted-foreground">Loading verification…</div>
        )}
        {isError && (
          <div className="p-6 text-sm text-red-600">
            Failed to load verification details.
          </div>
        )}

        {alert && (
          <div className="space-y-5 p-4">
            {/* Header */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={severityVariant(alert.severity)}>
                  {alert.severity}
                </Badge>
                <Badge variant="online">{alert.alert_type || alert.rule_type}</Badge>
                {typeof alert.escalation_level === "number" &&
                  alert.escalation_level > 0 && (
                    <Badge variant="warning">L{alert.escalation_level}</Badge>
                  )}
              </div>
              <h2 className="text-lg font-semibold leading-tight">{alert.title}</h2>
              <p className="text-xs text-muted-foreground">
                {alert.camera_id
                  ? `cam:${alert.camera_id.slice(0, 8)}…`
                  : alert.site_id
                    ? `site:${alert.site_id.slice(0, 8)}…`
                    : "—"}{" "}
                · {formatDate(alert.created_at)}
              </p>
              <p className="text-sm">{alert.message}</p>
            </div>

            {/* Live camera tile */}
            <section className="space-y-1">
              <h3 className="text-xs font-medium uppercase text-muted-foreground">
                Live camera
              </h3>
              {data?.signed_stream_url ? (
                <CameraFeedPlayer
                  hlsUrl={data.signed_stream_url}
                  online
                  cameraName={alert.title}
                  className="aspect-video w-full overflow-hidden rounded-md bg-black"
                />
              ) : (
                <div className="flex aspect-video w-full items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
                  No live stream available
                </div>
              )}
            </section>

            {/* Latest snapshot + bbox overlay */}
            {data?.latest_snapshot_url && (
              <section className="space-y-1">
                <h3 className="text-xs font-medium uppercase text-muted-foreground">
                  Latest snapshot
                </h3>
                <div className="relative w-full overflow-hidden rounded-md border bg-black">
                  <img
                    src={data.latest_snapshot_url}
                    alt="Latest detection snapshot"
                    className="w-full"
                  />
                  {bbox && <BoundingBoxOverlay box={bbox} />}
                </div>
              </section>
            )}

            {/* Runbook panel */}
            {data?.runbook && (
              <section className="space-y-2 rounded-md border bg-muted/30 p-3">
                <h3 className="text-sm font-semibold">SOP · {data.runbook.title}</h3>
                {data.runbook.steps.length > 0 && (
                  <ol className="list-decimal space-y-1 pl-5 text-sm">
                    {data.runbook.steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                )}
                {data.runbook.contacts.length > 0 && (
                  <div className="space-y-1 pt-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">
                      Contacts
                    </p>
                    {data.runbook.contacts.map((c, i) => (
                      <p key={i} className="text-xs">
                        <span className="font-medium">{c.name}</span>
                        {c.role ? ` · ${c.role}` : ""}
                        {c.phone ? ` · ${c.phone}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Auto-captured clip link */}
            {alert.auto_clip_id && (
              <Link
                to="/evidence"
                className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              >
                <FilmIcon className="h-4 w-4" />
                View auto-captured clip →
              </Link>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 border-t pt-4">
              <Button
                className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={ack.isPending || alert.status === "resolved"}
                onClick={() => ack.mutate(alert.id)}
              >
                Acknowledge
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate(alert.id, { onSuccess: onClose })}
              >
                Dismiss
              </Button>
              <Button
                className={cn(
                  "flex-1 bg-orange-500 text-white hover:bg-orange-600",
                )}
                disabled={escalate.isPending}
                title="Bump to the next escalation level now (unified escalation path)"
                onClick={() => escalate.mutate(alert.id)}
              >
                {escalate.isPending ? "Escalating…" : "Escalate now"}
              </Button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
